import { ChangeEvent, useMemo, useState } from "react"
import { useSnackbar } from "notistack"

import { useProductStock } from "../../../../../../shared/components/productAvailability/useProductStock"
import {
    checkProductStock,
    getMaxQuantity,
    getProductLeftCount,
} from "../../../../../../shared/components/productAvailability/stockHelpers"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import {
    CUSTOM_PRODUCT_OPTION_ID,
    ProductionEditLine,
    ProductionProductOption,
    SetLineProductInput,
} from "./useProductionOrderEdit"

type Params = Readonly<{
    line: ProductionEditLine
    productOptions: ReadonlyArray<ProductionProductOption>
    usedProductIds: ReadonlyArray<number>
    onCountChange: (key: string, value: number) => void
    onProductChange: (key: string, product: SetLineProductInput) => void
}>

type OptionStock = Readonly<{ soldOut: boolean; label: string | undefined }>

/** Per-line stock/availability guards for the production quick-edit product picker (mirrors Create order). */
export function useProductionLineStock({
    line,
    productOptions,
    usedProductIds,
    onCountChange,
    onProductChange,
}: Params) {
    const { l10n } = useAppLocalization()
    const { enqueueSnackbar } = useSnackbar()
    const [isCountEmpty, setIsCountEmpty] = useState(false)

    const productOption = useMemo(
        () => productOptions.find((option) => option.productId === line.productId),
        [productOptions, line.productId],
    )
    const availability = productOption?.availability
    const isUnavailable = availability !== undefined && !availability.isAvailable
    const isInactiveProduct = line.productId !== undefined && !line.isExtraService && productOption === undefined
    const { disableIncrease: isStockLimitReached, validateQuantity } = useProductStock({
        stock: productOption?.stock,
        currentQuantity: line.count,
        isUnavailable,
    })

    const notifyStockLimit = (reason: "daily-limit" | "stock-limit" | null) =>
        enqueueSnackbar(
            l10n.getString(
                reason === "daily-limit"
                    ? "product-daily-limit-exceeded-snackbar"
                    : "product-stock-limit-exceeded-snackbar",
            ),
            { variant: "warning" },
        )

    // A stock limit shows the daily/stock snackbar; a non-stock unavailability falls back to the product's message.
    const allowIncrease = (quantity: number): boolean => {
        if (isInactiveProduct) return false
        const check = validateQuantity(quantity)
        if (!check.allowed) {
            notifyStockLimit(check.reason)
            return false
        }
        if (availability && !availability.isAvailable) {
            enqueueSnackbar(availability.message, { variant: "warning" })
            return false
        }
        return true
    }

    const handleCountInput = (event: ChangeEvent<HTMLInputElement>) => {
        const digits = event.target.value.replace(/\D/g, "")
        setIsCountEmpty(!digits)
        if (!digits) return
        const value = parseInt(digits, 10)
        if (value > line.count && !allowIncrease(value)) return
        onCountChange(line.key, value)
    }

    const handleCountBlur = () => setIsCountEmpty(false)

    const handleIncrease = () => {
        const next = line.count + 1
        if (!allowIncrease(next)) return
        onCountChange(line.key, next)
    }

    const isOptionSoldOut = (option: ProductionProductOption) =>
        (option.availability !== undefined && !option.availability.isAvailable) ||
        (getMaxQuantity(option.stock) ?? Number.POSITIVE_INFINITY) <= 0

    const getOptionStock = (option: ProductionProductOption): OptionStock => {
        if (isOptionSoldOut(option)) {
            const label =
                option.availability && !option.availability.isAvailable
                    ? option.availability.shortMessage
                    : l10n.getString("meeting-service-production-view-product-out-of-stock")
            return { soldOut: true, label }
        }
        const leftCount = getProductLeftCount(option.stock)
        const label =
            leftCount !== undefined ? l10n.getString("offer-tag-limited-on-sale", { amount: leftCount }) : undefined
        return { soldOut: false, label }
    }

    const handleProductSelect = (value: ProductionProductOption | null) => {
        // Ignore re-selecting the wish's own placeholder; only a real catalog product replaces the line.
        if (!value || value.productId === CUSTOM_PRODUCT_OPTION_ID) return

        // The same product may exist only once per delivery, so an already-used one is rejected rather than hidden.
        if (value.productId !== line.productId && usedProductIds.includes(value.productId)) {
            enqueueSnackbar(l10n.getString("meeting-service-production-view-product-already-added-snackbar"), {
                variant: "warning",
            })
            return
        }

        // Match Create order: block unavailable/out-of-stock on select; clamp to what's available.
        if (value.availability && !value.availability.isAvailable) {
            enqueueSnackbar(value.availability.message, { variant: "warning" })
            return
        }
        const stockCheck = checkProductStock(value.stock, line.count)
        if (!stockCheck.allowed && (stockCheck.maxQuantity ?? 0) <= 0) {
            notifyStockLimit(stockCheck.reason)
            return
        }

        onProductChange(line.key, { productId: value.productId, name: value.name, price: value.itemPrice })

        if (!stockCheck.allowed) {
            notifyStockLimit(stockCheck.reason)
            onCountChange(line.key, stockCheck.maxQuantity ?? 0)
        }
    }

    return {
        disableIncrease: isStockLimitReached || isInactiveProduct,
        isInactiveProduct,
        countInputValue: isCountEmpty ? "" : line.count,
        handleCountInput,
        handleCountBlur,
        handleIncrease,
        isOptionSoldOut,
        getOptionStock,
        handleProductSelect,
    }
}
